/**
 * Hero Slider section
 * Vanilla JS, namespaced, supports multiple instances per page and the
 * Shopify Theme Editor's section/block lifecycle events.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-hero-slider]';
  var SLIDE_SELECTOR = '[data-hero-slide]';
  var DOT_SELECTOR = '[data-hero-dot]';
  var ACTIVE_SLIDE_CLASS = 'hero-slider__slide--active';
  var ACTIVE_DOT_CLASS = 'hero-slider__dot--active';

  function HeroSlider(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll(SLIDE_SELECTOR));
    this.dots = Array.prototype.slice.call(root.querySelectorAll(DOT_SELECTOR));
    this.prevBtn = root.querySelector('[data-hero-prev]');
    this.nextBtn = root.querySelector('[data-hero-next]');

    var activeIndex = this.slides.findIndex(function (slide) {
      return slide.classList.contains(ACTIVE_SLIDE_CLASS);
    });
    this.current = activeIndex > -1 ? activeIndex : 0;

    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseFloat(root.dataset.autoplaySpeed) || 6) * 1000;
    this.timer = null;
    this.editorPaused = false;

    this.handleNextClick = this.handleNextClick.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleDotClick = this.handleDotClick.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.bindEvents();
    this.startAutoplay();
  }

  HeroSlider.prototype.bindEvents = function () {
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', this.handleNextClick);
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this.handlePrevClick);
    }
    this.dots.forEach(function (dot) {
      dot.addEventListener('click', this.handleDotClick);
    }, this);

    this.root.addEventListener('shopify:block:select', this.handleBlockSelect);
    this.root.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  };

  HeroSlider.prototype.unbindEvents = function () {
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.handleNextClick);
    }
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.handlePrevClick);
    }
    this.dots.forEach(function (dot) {
      dot.removeEventListener('click', this.handleDotClick);
    }, this);

    this.root.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.root.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  };

  HeroSlider.prototype.handleNextClick = function () {
    this.goToSlide(this.current + 1);
    this.restartAutoplay();
  };

  HeroSlider.prototype.handlePrevClick = function () {
    this.goToSlide(this.current - 1);
    this.restartAutoplay();
  };

  HeroSlider.prototype.handleDotClick = function (event) {
    var index = parseInt(event.currentTarget.getAttribute('data-index'), 10);
    if (Number.isNaN(index)) return;
    this.goToSlide(index);
    this.restartAutoplay();
  };

  HeroSlider.prototype.handleBlockSelect = function (event) {
    var slide = event.target.closest ? event.target.closest(SLIDE_SELECTOR) : null;
    if (!slide) return;
    var index = parseInt(slide.getAttribute('data-index'), 10);
    if (Number.isNaN(index)) return;
    this.editorPaused = true;
    this.stopAutoplay();
    this.goToSlide(index);
  };

  HeroSlider.prototype.handleBlockDeselect = function () {
    this.editorPaused = false;
    this.startAutoplay();
  };

  HeroSlider.prototype.handleVisibilityChange = function () {
    if (document.hidden) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  };

  HeroSlider.prototype.goToSlide = function (index) {
    var length = this.slides.length;
    if (!length) return;

    var nextIndex = ((index % length) + length) % length;
    if (nextIndex === this.current) return;

    var currentSlide = this.slides[this.current];
    var nextSlide = this.slides[nextIndex];

    if (currentSlide) {
      currentSlide.classList.remove(ACTIVE_SLIDE_CLASS);
      currentSlide.setAttribute('aria-hidden', 'true');
    }
    if (nextSlide) {
      nextSlide.classList.add(ACTIVE_SLIDE_CLASS);
      nextSlide.removeAttribute('aria-hidden');
    }

    var currentDot = this.dots[this.current];
    var nextDot = this.dots[nextIndex];
    if (currentDot) {
      currentDot.classList.remove(ACTIVE_DOT_CLASS);
      currentDot.setAttribute('aria-selected', 'false');
    }
    if (nextDot) {
      nextDot.classList.add(ACTIVE_DOT_CLASS);
      nextDot.setAttribute('aria-selected', 'true');
    }

    this.current = nextIndex;
  };

  HeroSlider.prototype.startAutoplay = function () {
    if (!this.autoplayEnabled || this.slides.length < 2 || this.editorPaused || document.hidden) {
      return;
    }
    this.stopAutoplay();
    var self = this;
    this.timer = window.setInterval(function () {
      self.goToSlide(self.current + 1);
    }, this.autoplaySpeed);
  };

  HeroSlider.prototype.stopAutoplay = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  HeroSlider.prototype.restartAutoplay = function () {
    this.stopAutoplay();
    this.startAutoplay();
  };

  HeroSlider.prototype.destroy = function () {
    this.stopAutoplay();
    this.unbindEvents();
  };

  function initHeroSliders(scope) {
    var context = scope || document;
    var roots = context.matches && context.matches(ROOT_SELECTOR)
      ? [context]
      : Array.prototype.slice.call(context.querySelectorAll(ROOT_SELECTOR));

    roots.forEach(function (root) {
      if (!root.heroSliderInstance) {
        root.heroSliderInstance = new HeroSlider(root);
      }
    });
  }

  function teardownHeroSliders(scope) {
    var context = scope || document;
    var roots = context.matches && context.matches(ROOT_SELECTOR)
      ? [context]
      : Array.prototype.slice.call(context.querySelectorAll(ROOT_SELECTOR));

    roots.forEach(function (root) {
      if (root.heroSliderInstance) {
        root.heroSliderInstance.destroy();
        root.heroSliderInstance = null;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initHeroSliders(document);
    });
  } else {
    initHeroSliders(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initHeroSliders(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardownHeroSliders(event.target);
  });
})();